<template>
  <v-dialog
    v-model="internalDialogVisible"
    max-width="835px"
    :fullscreen="isMobileBreakpoint"
    persistent
    content-class="panda-assist-dialog"
    @input="handleDialogClose"
  >
    <v-col v-if="step < 6" class="d-flex justify-space-between pb-0">
      <v-icon
        v-if="step > 1 && step < 5"
        @click="backStep"
        class="close-icon ml-2"
        size="24"
        color="grey"
        style="cursor: pointer"
      >
        mdi-arrow-left
      </v-icon>
      <v-icon
        v-if="step !== 5"
        @click="closeModal"
        class="close-icon ml-auto"
        size="24"
        color="grey"
        style="cursor: pointer"
      >
        mdi-close
      </v-icon>
    </v-col>
    <v-card
      flat
      style="height: 533px; max-width: 385px; margin: 0 auto"
      class="d-flex flex-column align-center justify-center"
      v-if="step < 5"
    >
      <simple-svg
        v-if="step === 0"
        width="80px"
        height="80px"
        :src="require('@/assets/pandaAssistModal.svg')"
      />

      <v-card-text class="pa-0">
        <div v-if="step > 0 && step < 5" class="chatPreview">
          <div
            class="d-flex justify-space-between align-center"
            style="
              padding: 8px 16px;
              background: var(--brand-0, #02061c);
              border-top-right-radius: 8px;
              border-top-left-radius: 8px;
              color: #ffff;
              font-size: 11px;
            "
          >
            <span style="text-transform: uppercase">{{
              name || $t('message.pages.pandaAssist.createModal.chatName')
            }}</span>
            <span>
              <v-icon size="16" color="white"> mdi-open-in-new </v-icon>
            </span>
          </div>
          <div
            v-if="step > 2"
            class="mx-4 mt-4 pa-2"
            style="
              border-radius: 16px 16px 16px 0px;
              background: var(--neutral-30-base, #3f404c);
              color: #edeef1;
              white-space: pre-wrap;
              word-break: break-word;
            "
          >
            {{
              initialQuestion ||
              $t('message.pages.pandaAssist.createModal.questionHere')
            }}
          </div>
          <div
            v-if="step > 3"
            class="mx-4 mt-4 ml-auto"
            style="
              width: 188px;
              height: 95px;
              border-radius: 8px;
              color: #edeef1;
            "
            :style="{ background: suggestionEnabled ? '#FFFF' : '' }"
          >
            <div class="pa-3" style="display: grid" v-if="suggestionEnabled">
              <span class="questionSugestion">
                {{
                  $t(
                    'message.pages.pandaAssist.createModal.questionsSuggestionsTitle'
                  )
                }}
              </span>
              <base-btn
                type="tertiary"
                size="small"
                class="mt-4 ml-auto justify-content-end"
              >
                {{
                  $t('message.pages.pandaAssist.createModal.firstSuggestions')
                }}
                <v-icon class="ml-1" size="20"> mdi-arrow-right </v-icon>
              </base-btn>
            </div>
          </div>
        </div>
      </v-card-text>

      <div
        v-if="step !== 0 && step < 5"
        class="step-indicators d-flex justify-center mt-6 mb-5"
      >
        <div
          v-for="s in totalSteps"
          :key="s"
          :class="['step-indicator', { active: step === s }]"
        ></div>
      </div>

      <v-card-title class="pa-0">
        <span class="assistHeadline">
          <template v-if="step === 0">
            <div class="text-center body-bold-xlarge mb-6">
              <v-tag
                type="default"
                size="xsmall"
                class="ml-1 my-2"
                style="display: inline-block"
              >
                BETA
              </v-tag>
              <br />
              {{ $t('message.pages.pandaAssist.createModal.talkToYourVideo') }}
            </div>
          </template>

          <template v-else-if="step === 1">
            {{ $t('message.pages.pandaAssist.createModal.nameStart') }}
          </template>
          <template v-else-if="step === 2">
            {{ $t('message.pages.pandaAssist.createModal.choseLang') }}
          </template>
          <template v-else-if="step === 3">
            {{ $t('message.pages.pandaAssist.createModal.initialQuestion') }}
          </template>
          <template v-else-if="step === 4">
            {{
              $t('message.pages.pandaAssist.createModal.questionsSuggestions')
            }}
          </template>
          <template v-else-if="step === 5">
            {{ $t('message.pages.pandaAssist.createModal.selectVideos') }}
          </template>
        </span>
      </v-card-title>

      <v-card-text class="text-center" style="padding: 0">
        <!-- Etapa 1 -->
        <div v-if="step === 0">
          <p class="textType6--text body-regular-base mb-6">
            {{
              $t('message.pages.pandaAssist.createModal.nowProvideKnowledge')
            }}
          </p>
          <div class="d-flex flex-column align-center mt-5">
            <base-btn type="primary" @click="nextStep" class="mb-2">
              {{ $t('message.pages.pandaAssist.createModal.createPanda') }}
            </base-btn>
            <base-btn type="neutral" @click="closeModal">
              {{ $t('message.pages.pandaAssist.createModal.createLater') }}
            </base-btn>
            <span class="mt-3 message-box" :key="messageKey">
              {{ currentMessage }}
            </span>
          </div>
        </div>

        <!-- Etapa 2 -->
        <div class="d-flex flex-column align-center" v-else-if="step === 1">
          <p class="textType6--text">
            {{ $t('message.pages.pandaAssist.createModal.topOfChat') }}
          </p>
          <v-input
            style="width: 100%"
            v-model="name"
            :maxlength="35"
            :placeholder="
              $t('message.pages.pandaAssist.createModal.enterNamePlaceholder')
            "
          ></v-input>
          <base-btn type="secondary" block @click="nextStep" :disabled="!name">
            {{ $t('message.pages.pandaAssist.createModal.continue') }}
          </base-btn>
        </div>

        <div v-if="step === 2">
          <p class="textType6--text body-regular-small">
            {{ $t('message.pages.pandaAssist.createModal.usedForKnowledge') }}
          </p>
          <v-select
            v-model="langOption"
            placeholder="placeholder"
            :items="items"
            hide-details
            style="width: 252px; margin: 0 auto"
          >
            <template #prepend-inner>
              <img class="mt-1 mr-2" :src="currentIcon" />
            </template>
          </v-select>
          <div class="d-flex flex-column align-center mt-5">
            <base-btn type="secondary" @click="nextStep" class="mb-2">
              {{ $t('message.pages.pandaAssist.createModal.continue') }}
            </base-btn>
          </div>
        </div>

        <!-- Etapa 3 -->
        <div
          class="d-flex flex-column align-center max-width"
          v-else-if="step === 3"
        >
          <p>
            {{ $t('message.pages.pandaAssist.createModal.openWithAudience') }}
          </p>
          <v-textarea
            key="langOption"
            style="width: 100%"
            v-model="initialQuestion"
            :rows="calculatedRows"
            no-resize
            counter="250"
            :maxlength="250"
            :placeholder="
              $t(
                'message.pages.pandaAssist.createModal.enterInitialQuestionPlaceholder'
              )
            "
            @input="updateRows"
          />
          <base-btn
            type="secondary"
            block
            @click="nextStep"
            :disabled="!initialQuestion"
          >
            {{ $t('message.pages.pandaAssist.createModal.continue') }}
          </base-btn>
        </div>

        <!-- Etapa 4 -->
        <div class="d-flex flex-column align-center" v-else-if="step === 4">
          <p class="mb-0">
            {{
              $t('message.pages.pandaAssist.createModal.automaticSuggestions')
            }}
          </p>
          <v-switch
            v-model="suggestionEnabled"
            :label="$t('message.pages.pandaAssist.createModal.activate')"
          ></v-switch>
          <base-btn type="secondary" block @click="nextStep">
            {{ $t('message.pages.pandaAssist.createModal.continue') }}</base-btn
          >
        </div>
      </v-card-text>
    </v-card>
    <div
      v-if="(step === 5 || step === 6) && isMobileBreakpoint"
      class="d-flex justify-space-between px-3 pt-2"
    >
      <span class="d-flex">
        <v-icon
          @click="backStep"
          class="close-icon"
          size="20"
          style="cursor: pointer"
        >
          mdi-arrow-left
        </v-icon>
        <div class="body-bold-small ml-2">
          <span v-if="step === 5">{{
            $t('message.pages.pandaAssist.createModal.mobileSelectVideos')
          }}</span>
          <span v-if="step === 6">{{
            $t('message.pages.pandaAssist.createModal.mobileCheckoutOverview')
          }}</span>
        </div>
      </span>
      <v-icon
        @click="closeModal"
        class="close-icon ml-auto"
        size="20"
        color="grey"
        style="cursor: pointer"
      >
        mdi-close
      </v-icon>
    </div>
    <v-card class="px-6" flat v-if="step === 5" style="min-height: 700px">
      <!-- Etapa 5 -->
      <div :class="isMobileBreakpoint ? 'pa-3' : ''">
        <div class="d-flex mb-6 pt-3">
          <div>
            <v-card-title class="pa-0 ma-0 mb-1">
              <span
                :class="
                  isMobileBreakpoint ? 'body-bold-small' : 'body-bold-medium'
                "
              >
                {{ $t('message.pages.pandaAssist.createModal.selectVideos') }}
              </span>
            </v-card-title>
            <v-card-text
              class="pa-0 textType6--text"
              :class="
                isMobileBreakpoint
                  ? 'body-regular-smaller'
                  : 'body-regular-small,'
              "
            >
              {{
                $t(
                  'message.pages.pandaAssist.createModal.creditExhaustedExplanation'
                )
              }}
              <a target="_blank" type="secondary" :href="embedArticle">
                <span>
                  {{ $t('message.embedModal.knowMore') }}
                </span>
                <v-icon size="12">mdi-open-in-new</v-icon>
              </a>
            </v-card-text>
          </div>
          <v-icon
            v-if="!isMobileBreakpoint"
            @click="closeModal"
            class="close-icon ml-auto"
            size="24"
            color="grey"
            style="cursor: pointer"
          >
            mdi-close
          </v-icon>
        </div>
        <v-input
          ref="autoCompleteInput"
          hide-details="auto"
          class="pb-4"
          prepend-inner-icon="panda-search"
          v-model="searchQuery"
          :placeholder="
            $t('message.pages.pandaAssist.createModal.searchPlaceholder')
          "
        ></v-input>
        <template v-if="showPath">
          <small v-for="(item, i) in breadcrumbArray" :key="i">
            <button class="pb-3" type="button" @click="handleBackTo(item)">
              <span :class="i === 0 ? 'body-bold-small' : 'textType6--text'">
                <v-icon
                  v-if="i === 0 && breadcrumbArray.length > 1"
                  class="tableArrow"
                  size="16"
                  color="grey"
                  style="cursor: pointer"
                >
                  mdi-arrow-left
                </v-icon>
                {{
                  item.id
                    ? item.name
                    : $t('message.pages.pandaAssist.createModal.library')
                }}
                <span
                  class="textType6--text px-1"
                  v-if="i < breadcrumbArray.length - 1"
                >
                  /
                </span>
              </span>
            </button>
          </small>
          <small>
            <v-icon v-if="!!selectedFolder">mdi-chevron-right</v-icon>
            <button v-if="!!selectedFolder" type="button" class="primary--text">
              <span class="breadcrumbTexts primary--text">
                {{ selectedFolder.name }}
              </span>
            </button>
          </small>
        </template>

        <v-row no-gutters v-if="showPath && filteredItems.length > 0">
          <v-card
            class="d-flex align-center justify-center ml-1"
            width="50px"
            flat
          >
            <v-simple-checkbox
              :indeterminate="checkboxIndeterminateStatus"
              :value="areAllSelected"
              :ripple="false"
              @input="toggleSelectAll"
            />
          </v-card>
          <v-card
            flat
            :style="{ width: isMobileBreakpoint ? '50px' : '465px' }"
            class="d-flex align-center justify-start"
          >
            <v-row no-gutters>
              <div class="d-flex align-center">
                <span class="body-bold-smaller mr-1">
                  {{ $t('message.pages.videosDataTable.header.name') }}
                </span>
              </div>
            </v-row>
          </v-card>
          <v-card
            v-if="!isMobileBreakpoint"
            class="d-flex align-center"
            height="45px"
            flat
          >
            <span class="body-bold-smaller pr-1">
              {{ $i18next.t('message.pages.videosDataTable.header.time') }}
            </span>
          </v-card>
          <v-card
            v-if="!isMobileBreakpoint"
            style="justify-content: end"
            class="d-flex align-center"
            height="45px"
            flat
            width="170px"
          >
            <div class="d-flex align-center">
              <span class="body-bold-smaller" style="margin-right: 4px">
                Valor
              </span>
            </div>
          </v-card>
        </v-row>
        <vue-virtual-table
          v-if="filteredItems.length > 0"
          ref="dataTableContent"
          class="data-table__content"
          :config="headers"
          :data="filteredItems"
          :height="tableHeight"
          :itemHeight="48"
          :minWidth="770"
        >
          <template slot-scope="scope" slot="select">
            <v-row no-gutters align="center" justify="end">
              <v-simple-checkbox
                class="pl-3"
                color="primary"
                :ripple="false"
                :disabled="
                  alreadyHasPandaAssistVideos.includes(scope.row.id) ||
                  checkVideoSelected(scope)
                "
                :value="checkIfSelected(scope.row)"
                @input="checkboxSelection(scope.row)"
              ></v-simple-checkbox>
            </v-row>
          </template>
          <template slot-scope="scope" slot="titleCommon">
            <v-row
              no-gutters
              align="center"
              class="searchVideosMenuContext flex-nowrap"
              @click="handleItemClick(scope.row)"
            >
              <v-icon class="folderIcon ml-2" v-if="scope.row.name" size="16">
                mdi-folder
              </v-icon>
              <v-icon
                class="ml-2"
                :class="
                  alreadyHasPandaAssistVideos.includes(scope.row.id)
                    ? 'videoIconNeutral'
                    : 'videoIcon'
                "
                v-else
                size="16"
              >
                panda-video
              </v-icon>
              <span
                class="textType6--text ml-2 truncate"
                :class="
                  alreadyHasPandaAssistVideos.includes(scope.row.id)
                    ? 'videoIconNeutral'
                    : ''
                "
                :title="scope.row.name ? scope.row.name : scope.row.title"
                v-html="
                  highlightMatch(
                    scope.row.name ? scope.row.name : scope.row.title
                  )
                "
              ></span>
            </v-row>
          </template>
          <template slot-scope="scope" slot="durationCommon">
            <v-row
              no-gutters
              align="center"
              justify="end"
              class="searchVideosMenuContext body-regular-smaller"
              @click="handleEnterFolder(scope.row)"
            >
              <span v-if="scope.row.name">
                {{
                  folderData.get(scope.row.id)
                    ? folderData.get(scope.row.id).length
                      ? formatTime(folderData.get(scope.row.id).length)
                      : '00:00'
                    : '...'
                }}
              </span>
              <span v-else>
                {{
                  scope.row.length ? formatDuration(scope.row.length) : '00:00'
                }}
              </span>
            </v-row>
          </template>
          <template slot-scope="scope" slot="iaCommon">
            <v-row
              no-gutters
              align="center"
              class="searchVideosMenuContext justify-end"
              @click="handleEnterFolder(scope.row)"
            >
              <span v-if="alreadyHasPandaAssistVideos.includes(scope.row.id)">
                <v-tag type="neutral">
                  {{ $t('message.pages.pandaAssist.createModal.alreadyHave') }}
                </v-tag>
              </span>
              <span class="mr-4" v-else-if="scope.row.original_lang">
                <v-tag type="success">
                  {{ $t('message.pages.pandaAssist.createModal.subtitle') }}
                  <v-icon
                    :color="$vuetify.theme.dark ? '#DCEFDD' : '#174F1B'"
                    class="ml-1"
                  >
                    panda-check-round
                  </v-icon>
                </v-tag>
              </span>

              <!-- Exibe a duração em minutos -->

              <span class="body-bold-smaller">
                <span v-if="alreadyHasPandaAssistVideos.includes(scope.row.id)">
                </span>
                <span
                  v-else-if="scope.row.original_lang"
                  class="body-bold-smaller textType5--text mr-2"
                >
                  0
                </span>
                <span v-else-if="scope.row.name" class="mr-2">
                  {{
                    folderData.get(scope.row.id)
                      ? folderData.get(scope.row.id).cost
                      : '...'
                  }}
                </span>
                <span v-else class="body-bold-smaller textType5--text mr-8">
                  {{ calcCreditsPerVideo(scope.row) }}
                </span>
              </span>
              <v-icon
                v-if="!alreadyHasPandaAssistVideos.includes(scope.row.id)"
                class="creditsIcon"
                size="16"
                >panda-ia-credits</v-icon
              >
            </v-row>
          </template>
        </vue-virtual-table>
        <div
          v-else
          style="height: 450px"
          class="empty-state d-flex align-center justify-center"
        >
          <span style="display: grid; justify-items: center">
            <p class="body-regular-base d-flex justify-center mb-2">
              {{
                showPath
                  ? $t('message.pages.pandaAssist.createModal.noItemsFoundAdd')
                  : $t('message.pages.pandaAssist.createModal.noItemsFound')
              }}
            </p>

            <p
              style="text-align: center"
              v-html="
                showPath
                  ? $t(
                      'message.pages.pandaAssist.createModal.noItemsFoundDescriptionAdd'
                    )
                  : $t(
                      'message.pages.pandaAssist.createModal.noItemsFoundDescription'
                    )
              "
              class="body-regular-small textType6--text"
            ></p>
            <base-btn
              v-if="!showPath"
              type="tertiary"
              size="small"
              @click="
                () => {
                  searchQuery = ''
                  applyFilter('')
                }
              "
              >{{
                $t('message.pages.pandaAssist.createModal.cleanSearch')
              }}</base-btn
            >
          </span>
        </div>
      </div>
    </v-card>
    <v-card
      class="modalFooter d-flex justify-space-between"
      height="88px"
      flat
      v-if="step === 5"
    >
      <div>
        <base-btn type="tertiary" @click="finalize" class="mr-2">
          {{ $t('message.pages.pandaAssist.createModal.cancel') }}
        </base-btn>
      </div>
      <div class="d-flex">
        <div style="display: grid">
          <span class="body-bold-smaller textType5--text ml-auto"
            >{{ $t('message.pages.pandaAssist.createModal.total') }}:
            <span class="ml-2">
              {{ totalMinutes }}
              <v-icon class="creditsIcon" size="16"
                >panda-ia-credits</v-icon
              ></span
            ></span
          >
          <span
            v-if="selectedItems.length === 0"
            class="body-regular-smaller textType6--text"
            >{{
              $t('message.pages.pandaAssist.createModal.noVideosSelected')
            }}</span
          >
          <span class="body-regular-smaller textType6--text" v-else>
            {{ videoSelectionText }}
          </span>
        </div>
        <base-btn
          :disabled="
            selectedItems.filter((item) => item.title).length === 0 || isLoading
          "
          type="primary"
          @click="nextStepOverview"
          class="ml-6"
        >
          <template v-if="isLoading">
            <v-progress-circular indeterminate size="20" class="mr-2" />
            {{ $t('message.pages.pandaAssist.createModal.processing') }}
          </template>
          <template v-else>
            {{ $t('message.pages.pandaAssist.createModal.confirm') }}
          </template>
        </base-btn>
      </div>
    </v-card>
    <v-card style="height: 560px; width: 822px" flat v-if="step === 6">
      <v-row no-gutters class="checkoutStep">
        <v-col
          v-if="!isMobileBreakpoint"
          class="checkoutDesc pt-6 pl-6"
          cols="6"
        >
          <v-icon
            @click="backStep"
            size="24"
            color="grey"
            style="cursor: pointer"
          >
            mdi-arrow-left
          </v-icon>
          <br />
          <div
            style="
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 80%;
            "
          >
            <simple-svg
              width="80px"
              height="80px"
              :src="require('@/assets/pandaAssistModal.svg')"
            />
            <span class="body-bold-small mt-4">Tutor</span>
            <p
              style="text-align: center"
              class="body-regular-smaller textType8--text"
            >
              {{
                $t(
                  'message.pages.pandaAssist.createModal.talkToYourVideoSubtitle'
                )
              }}
              <br />
              {{
                $t(
                  'message.pages.pandaAssist.createModal.talkToYourVideoSubtitle2'
                )
              }}
            </p>
          </div>
        </v-col>

        <v-col
          class="checkoutPayment px-0"
          :cols="isMobileBreakpoint ? 12 : 6"
          style="display: flex; flex-direction: column; height: 100%"
        >
          <!-- Conteúdo acima -->
          <div style="flex: 1">
            <div
              v-if="!isMobileBreakpoint"
              class="pa-6 justify-space-between d-flex"
            >
              <span class="body-bold-base">Tutor</span>
              <span>
                <v-icon
                  @click="closeModal"
                  class="close-icon ml-auto"
                  size="24"
                  color="grey"
                  style="cursor: pointer"
                >
                  mdi-close
                </v-icon>
              </span>
            </div>
            <div style="display: grid" class="py-4 pa-6">
              <span
                v-if="!isMobileBreakpoint"
                class="body-bold-smaller textType6--text pb-4"
                >{{
                  $t('message.pages.pandaAssist.createModal.purchaseSummary')
                }}</span
              >
              <span class="body-regular-smaller textType6--text d-flex">
                <span class="justify-space-between">
                  {{ purchaseOverviewData.num_of_videos }}
                  {{
                    $t('message.pages.pandaAssist.createModal.videoSubtitles')
                  }}
                  <v-tag
                    type="neutral"
                    class="ml-3"
                    size="xsmall"
                    style="display: inline-block"
                  >
                    {{ Math.ceil(purchaseOverviewData.total_seconds / 60) }} min
                  </v-tag>
                  <v-tag
                    type="neutral"
                    class="ml-2"
                    size="xsmall"
                    style="display: inline-block"
                  >
                    {{
                      $t(
                        'message.pages.pandaAssist.createModal.creditChargeValue'
                      )
                    }}
                  </v-tag>
                </span>
                <span class="ml-auto body-bold-smaller textType5--text">
                  {{ purchaseOverviewData.total_credit_cost }}
                  <v-icon class="creditsIcon" size="16"
                    >panda-ia-credits</v-icon
                  >
                </span>
              </span>
            </div>
          </div>

          <div style="display: grid" class="checkoutFooter pt-4 px-6">
            <span class="body-bold-smaller textType6--text pb-4">{{
              $t('message.pages.pandaAssist.createModal.valuePerResponse')
            }}</span>
            <p class="body-regular-smaller textType8--text">
              {{
                $t(
                  'message.pages.pandaAssist.createModal.responseValueExplanation'
                )
              }}
            </p>
            <p class="body-regular-smaller textType8--text">
              {{
                $t(
                  'message.pages.pandaAssist.createModal.creditExhaustedExplanation'
                )
              }}.
            </p>
            <span>
              <v-checkbox
                v-model="confirmValue"
                :label="
                  $t('message.pages.pandaAssist.createModal.understandValue')
                "
              ></v-checkbox>
            </span>
          </div>
          <div class="checkoutFooter py-4 px-6">
            <div class="justify-space-between d-flex">
              <span class="body-bold-base pb-2">Total</span>
              <span class="ml-auto body-bold-base textType5--text">
                {{ purchaseOverviewData.total_credit_cost }}
                <v-icon class="creditsIcon" size="16">panda-ia-credits</v-icon>
              </span>
            </div>
            <div class="justify-space-between d-flex">
              <span class="mb-4">
                <p class="body-regular-smaller textType8--text mb-1">
                  {{
                    $t(
                      'message.pages.pandaAssist.createModal.remainingBalance'
                    )
                  }}.
                </p>
                <p
                  v-if="remainingBalance() < 0"
                  style="cursor: pointer"
                  @click="openCredits"
                  class="body-bold-smaller btnTextType1--text mb-0"
                >
                  {{ $t('message.pages.pandaAssist.createModal.buyCredits') }}
                  <v-icon color="btnTextType1" size="12"
                    >mdi-open-in-new</v-icon
                  >
                </p>
              </span>
              <span
                v-if="remainingBalance() >= 0"
                class="body-bold-smaller textType5--text"
              >
                {{ remainingBalance() }}
                <v-icon class="creditsIcon" size="16">panda-ia-credits</v-icon>
              </span>
              <span v-else class="body-regular-smaller dangerCreditsText">
                {{ remainingBalance() }}
                <v-icon class="negativeCreditsIcon" size="16"
                  >panda-ia-credits</v-icon
                >
              </span>
            </div>
            <div class="d-flex">
              <base-btn type="tertiary" @click="finalize" class="mr-2">
                {{ $t('message.pages.pandaAssist.createModal.cancel') }}
              </base-btn>
              <base-btn
                v-if="confirmValue && remainingBalance() >= 0"
                type="primary"
                :disabled="isConfirming"
                style="width: 240px"
                @click="confirmAndBuy"
                class="ml-6"
              >
                <template v-if="isConfirming">
                  <v-progress-circular indeterminate size="20" class="mr-2" />
                  {{ $t('message.pages.pandaAssist.createModal.processing') }}..
                </template>
                <template v-else>
                  {{
                    $t('message.pages.pandaAssist.createModal.confirmAndBuy')
                  }}
                </template>
              </base-btn>

              <v-tooltip v-else-if="!isConfirming" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <base-btn
                    v-bind="attrs"
                    v-on="on"
                    disabled
                    type="primary"
                    style="width: 240px"
                    class="ml-6"
                  >
                    <template v-if="isConfirming">
                      <v-progress-circular
                        indeterminate
                        size="20"
                        class="mr-2"
                      />
                      {{
                        $t('message.pages.pandaAssist.createModal.processing')
                      }}..
                    </template>
                    <template v-else>
                      {{
                        $t(
                          'message.pages.pandaAssist.createModal.confirmAndBuy'
                        )
                      }}
                    </template>
                  </base-btn>
                </template>
                <v-container class="text-body-2 pa-0" style="max-width: 230px">
                  <span v-if="!confirmValue">
                    {{
                      $t(
                        'message.pages.pandaAssist.createModal.confirmCheckbox'
                      )
                    }}
                  </span>
                  <span v-if="remainingBalance() <= 0">
                    {{ $t('message.pages.pandaAssist.createModal.noCredits') }}
                  </span>
                </v-container>
              </v-tooltip>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-col
      v-if="step === 7"
      cols="12"
      class="d-flex justify-space-between pb-0"
    >
      <v-icon
        @click="backStep"
        class="close-icon ml-2"
        size="24"
        color="grey"
        style="cursor: pointer"
      >
        mdi-arrow-left
      </v-icon>
      <v-icon
        @click="closeModal"
        class="close-icon ml-auto"
        size="24"
        color="grey"
        style="cursor: pointer"
      >
        mdi-close
      </v-icon>
    </v-col>

    <v-card
      height="528px"
      flat
      class="d-flex flex-column align-center justify-center"
      v-if="step === 7"
    >
      <v-col
        class="d-flex flex-column align-center justify-center"
        style="text-align: center; width: 390px"
      >
        <v-icon size="56" color="#4CAF50">panda-check-round</v-icon>
        <v-card-title class="body-bold-medium py-0 pt-4 pb-1">
          <span>{{
            $t('message.pages.pandaAssist.createModal.successPurchase')
          }}</span>
        </v-card-title>
        <v-card-text class="textType6--text body-regular-smaller">
          {{ fixedDateTime }}
        </v-card-text>
        <v-card-text class="body-regular-small pt-0">
          {{
            $t('message.pages.pandaAssist.createModal.videoCreditPurchase', {
              minutes: purchaseOverviewData.total_credit_cost,
              credits: purchaseOverviewData.total_credit_cost,
            })
          }}
        </v-card-text>
        <base-btn @click="goToPandaAssist" type="primary">{{
          $t('message.pages.pandaAssist.createModal.goToPandaAssist')
        }}</base-btn>
        <base-btn @click="goToExtract" type="neutral" class="extractBtn mt-4">
          {{
            $t('message.pages.pandaAssist.createModal.openCreditExtract')
          }}</base-btn
        >
      </v-col>
    </v-card>
  </v-dialog>
</template>

<script>
import VueVirtualTable from 'vue-virtual-table'
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import i18n from '@/translations/lang'
import formatTimeMixin from '@/utils/formatTime.mixins.js'
import pandaAssist from '@/services/pandaAssist'
import videosApi from '@/services/videosApi'

export default {
  name: 'CreatePandaAssist',
  components: {
    VueVirtualTable,
  },
  props: {
    dialogVisible: {
      type: Boolean,
      required: true,
    },
    isUpdateVideos: {
      type: Object,
      required: false,
    },
    video_external_id: {
      type: [Number, String],
      default: '',
      required: false,
    },
    video_external: {
      type: Object,
    },
  },
  data() {
    return {
      internalDialogVisible: this.dialogVisible,
      step: 0,
      messages: [
        this.$t('message.pages.pandaAssist.messages.internalAssistant'),
        this.$t('message.pages.pandaAssist.messages.salesVSL'),
        this.$t('message.pages.pandaAssist.messages.knowledgeBase'),
        this.$t('message.pages.pandaAssist.messages.prepareStudents'),
        this.$t('message.pages.pandaAssist.messages.instantAnswers'),
      ],
      currentMessage: '',
      messageKey: 0,
      totalSteps: 6,
      searchQuery: '',
      fixedDateTime: null,
      isLoading: false,
      newAssistantId: null,
      isConfirming: false,
      minRows: 1,
      maxRows: 4,
      alreadyHasPandaAssistVideos: [],
      filteredItems: [],
      showPath: true,
      articles: {
        pt: 'https://help.pandavideo.com/pt-br/article/pandaassist-ai-como-criar-chats-com-ia-nos-seus-videos-zdy32s/',
        en: 'https://help.pandavideo.com/en-us/article/pandaassist-ai-how-to-create-ai-chats-in-your-videos-r4624o/',
        es: 'https://help.pandavideo.com/es/article/pandaassist-ai-como-crear-chats-con-ia-en-tus-videos-1ewhd32/',
      },
      langOption: 'pt-BR',
      allVideos: [],
      allVideosNotFiltered: [],
      totalMinutes: 0,
      items: [
        {
          text: this.$t('message.pages.pandaAssist.messages.portuguese'),
          value: 'pt-BR',
          icon: require('@/assets/brazil.svg'),
        },
        {
          text: this.$t('message.pages.pandaAssist.messages.english'),
          value: 'en',
          icon: require('@/assets/english.svg'),
        },
        {
          text: this.$t('message.pages.pandaAssist.messages.spanish'),
          value: 'es',
          icon: require('@/assets/spanish.svg'),
        },
      ],
      confirmValue: false,
      lastFolder: {
        name: '',
        id: null,
        parent_folder_id: null,
      },
      selectedFolder: null,
      searchFuseOptions: {
        keys: ['title', 'name', 'id', 'video_external_id', 'description'],
        includeScore: true,
        threshold: 0.4,
      },
      name: '',
      folderData: new Map(),
      purchaseOverviewData: [],
      flatItemList: [],
      suggestionEnabled: true,
      headers: [
        {
          prop: '_action',
          name: 'action',
          actionName: 'select',
          width: 45,
          alignItems: 'center',
          justifyContent: 'start',
        },
        {
          prop: '_action',
          name: 'title',
          actionName: 'titleCommon',
          width: 450,
        },
        {
          prop: '_action',
          name: 'goInto',
          actionName: 'durationCommon',
          width: 62,
          alignItems: 'center',
          justifyContent: 'end',
        },
        {
          prop: '_action',
          name: 'goInto',
          actionName: 'iaCommon',
          width: 196,
          alignItems: 'center',
          justifyContent: 'end',
        },
      ],
      selectedVideos: [],
      selectedItems: [],
      breadcrumbArray: [{ name: '', id: null, parent_folder_id: null }],
    }
  },
  mixins: [formatTimeMixin],
  computed: {
    ...mapState('user', {
      folders: 'mainFolders',
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    ...mapGetters('user', ['creditsBalance']),
    initialQuestion() {
      switch (this.langOption) {
        case 'pt-BR':
          return 'Como posso te ajudar hoje?'
        case 'en':
          return 'How can I help you today?'
        case 'es':
          return '¿Cómo puedo ayudarte hoy?'
        default:
          return this.$t('message.pages.pandaAssist.createModal.howCanIHelp')
      }
    },
    tableHeight() {
      let height = 500
      if (window.innerHeight < 850) {
        height = 450
      }
      if (window.innerHeight < 800) {
        height = 370
      }
      return height
    },
    calculatedRows() {
      const lines = this.initialQuestion.split('\n').length
      return Math.min(Math.max(lines, this.minRows), this.maxRows)
    },
    embedArticle() {
      const lang = this.$i18next.language || this.$i18n?.locale || 'en'
      switch (lang) {
        case 'pt-BR':
        case 'pt':
          return this.articles.pt
        case 'es':
          return this.articles.es
        case 'en':
        default:
          return this.articles.en
      }
    },
    videoSelectionText() {
      const count = this.selectedItems.filter((item) => item.title).length
      return this.$t(
        count === 1
          ? 'message.pages.pandaAssist.createModal.videoSelected'
          : 'message.pages.pandaAssist.createModal.videosSelected',
        { count }
      )
    },
    completeItems() {
      return [...this.folders, ...this.allVideos]
    },
    currentIcon() {
      const selectedItem = this.items.find(
        (item) => item.value === this.langOption
      )
      return selectedItem ? selectedItem.icon : require('@/assets/brazil.svg')
    },
    reducedBreadcrumbArray() {
      let arrayMod = this.breadcrumbArray.filter((folder) => {
        return folder.id !== this.lastFolder.id
      })
      if (this.selectedFolder) {
        return this.breadcrumbArray
      }
      return arrayMod
    },
    areAllSelected() {
      const selectableItems = this.filteredItems.filter(
        (item) => !this.alreadyHasPandaAssistVideos.includes(item.id)
      )
      return (
        selectableItems.length > 0 &&
        selectableItems.every((item) =>
          this.selectedItems.some((selected) => selected.id === item.id)
        )
      )
    },
    checkboxIndeterminateStatus() {
      const selectableItems = this.filteredItems.filter(
        (item) => !this.alreadyHasPandaAssistVideos.includes(item.id)
      )
      const selectedCount = selectableItems.filter((item) =>
        this.selectedItems.some((selected) => selected.id === item.id)
      ).length

      return selectedCount > 0 && selectedCount < selectableItems.length
    },
    lastBreadcrumbItem() {
      return this.breadcrumbArray[this.breadcrumbArray.length - 1]
    },
    isNamesTooBig() {
      let size = 0
      for (let folder of this.breadcrumbArray) {
        size += folder.name.length
      }
      if (this.selectedFolder?.name) {
        size += this.selectedFolder.name.length
      }
      return size > 50
    },
    allItems() {
      return this.toBeSelectedFolders.concat(this.allVideos)
    },
    allItemsNotFiltered() {
      return this.toBeSelectedFoldersRoot.concat(this.allVideosNotFiltered)
    },
    toBeSelectedFoldersRoot() {
      return this.folders
    },
    toBeSelectedFolders() {
      let selectedFolders = this.folders.filter((folder) => {
        return folder.parent_folder_id === this.lastFolder.id
      })
      return selectedFolders
    },
  },
  beforeDestroy() {
    this.$root.$off('pandaAssistVideos')
    this.$root.$off('pandaAssistAddVideos')
  },
  async mounted() {
    this.$root.$on('changeLanguage', () => {
      this.updateLocalizedTexts()
    })
    await this.checkIfVideosAreLoaded()
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
    this.$root.$on('pandaAssistVideos', (data) => {
      this.alreadyHasPandaAssistVideos = data
      this.checkIfVideosAreLoaded()
    })
    this.startMessageRotation()
    this.flatItemList = await this.flattenAllItems()
    document.addEventListener('keydown', this.checkIfCtrlAWasPressed)
    this.$root.$on('pandaAssistAddVideos', () => {
      if (this.isUpdateVideos) {
        const agentInfos = this.isUpdateVideos
        this.name = agentInfos.name
        this.initialQuestion = agentInfos.initial_question
        this.step = 5
      }
    })
  },
  async created() {
    if (this.$i18next.language === 'pt-BR' || this.$i18next.language === 'pt') {
      this.langOption = 'pt-BR'
    } else if (this.$i18next.language === 'en') {
      this.langOption = 'en'
    } else if (this.$i18next.language === 'es') {
      this.langOption = 'es'
    }
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
    this.updateCreditsBalance()
    this.getVideos()
    this.folderData = await this.getFolderSize(this.folders)
  },
  watch: {
    dialogVisible(newVal) {
      this.internalDialogVisible = newVal
    },
    step() {
      this.setFixedDateTime()
    },
    searchQuery(newQuery) {
      this.applyFilter(newQuery)
    },
    langOption() {
      this.$forceUpdate()
    },
    allItems: {
      immediate: true,
      handler(newItems) {
        this.filteredItems = newItems.filter(
          (item) =>
            item.status !== 'DELETING' &&
            item.status !== 'FAILED' &&
            item.status !== 'CONVERTING'
        )
      },
    },
    internalDialogVisible(newVal) {
      this.$emit('update:dialog-visible', newVal)
    },
    video_external_id() {
      this.filterByExternalID()
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('user', ['updateCreditsBalance']),
    highlightMatch(text) {
      if (!this.searchQuery) return text
      const regex = new RegExp(`(${this.searchQuery})`, 'gi')
      return text.replace(regex, '<b class="neutral-20-dark--text">$1</b>')
    },
    checkIfCtrlAWasPressed(event) {
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        const inputElement =
          this.$refs.autoCompleteInput?.$el?.querySelector('input')
        if (inputElement && this.searchQuery.length > 0) {
          inputElement.select()
          event.preventDefault()
        }
      }
    },
    closeModal() {
      this.internalDialogVisible = false
      if (this.video_external && Object.keys(this.video_external).length)
        this.$emit('buyConfirm', true)
      this.resetModal()
    },
    checkVideoSelected(scope) {
      return this.video_external?.id == scope.row.id
    },
    updateLocalizedTexts() {
      this.messages = [
        this.$t('message.pages.pandaAssist.messages.internalAssistant'),
        this.$t('message.pages.pandaAssist.messages.salesVSL'),
        this.$t('message.pages.pandaAssist.messages.knowledgeBase'),
        this.$t('message.pages.pandaAssist.messages.prepareStudents'),
        this.$t('message.pages.pandaAssist.messages.instantAnswers'),
      ]
      this.items = [
        {
          text: this.$t('message.pages.pandaAssist.messages.portuguese'),
          value: 'pt-BR',
          icon: require('@/assets/brazil.svg'),
        },
        {
          text: this.$t('message.pages.pandaAssist.messages.english'),
          value: 'en',
          icon: require('@/assets/english.svg'),
        },
        {
          text: this.$t('message.pages.pandaAssist.messages.spanish'),
          value: 'es',
          icon: require('@/assets/spanish.svg'),
        },
      ]
      this.messageKey = 0
      this.currentMessage = this.messages[this.messageKey]
    },
    goToExtract() {
      this.$router.push({ name: 'Credits' }).catch(() => {})
    },
    updateRows() {
      this.$nextTick(() => {
        const textarea = this.$refs.textarea?.$el.querySelector('textarea')
        if (textarea) {
          textarea.style.height = 'auto'
          textarea.style.height = `${textarea.scrollHeight}px`
        }
      })
    },
    setFixedDateTime() {
      if (!this.fixedDateTime) {
        const now = new Date()

        const locale = this.$i18n?.locale || this.$i18next?.language || 'pt-BR'

        const dateFormatter = new Intl.DateTimeFormat(locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })

        const timeFormatter = new Intl.DateTimeFormat(locale, {
          hour: '2-digit',
          minute: '2-digit',
        })

        const formattedDate = dateFormatter.format(now)
        const formattedTime = timeFormatter.format(now)

        const translationMap = {
          'pt-BR': 'às',
          en: 'at',
          es: 'a las',
        }

        const atTranslation = translationMap[locale] || 'às'

        this.fixedDateTime = `${formattedDate} ${atTranslation} ${formattedTime}`
      }
    },

    async flattenAllItems() {
      let allItems = []
      allItems.push(...this.folders)
      const videos = await window.db.videos
        .filter(({ status }) => status !== 'DELETING')
        .toArray()
      allItems.push(...videos)

      return allItems
    },
    openCredits() {
      this.$router.push({ name: 'Credits' }).catch(() => {})
    },
    handleItemClick(item) {
      if (this.alreadyHasPandaAssistVideos.includes(item.id)) {
        return
      }
      this.handleEnterFolder(item)
    },
    startMessageRotation() {
      let index = 0
      this.currentMessage = this.messages[index]

      setInterval(() => {
        index = (index + 1) % this.messages.length
        this.currentMessage = this.messages[index]
        this.messageKey++
      }, 5000)
    },

    remainingBalance() {
      return this.creditsBalance - this.purchaseOverviewData.total_credit_cost
    },

    applyFilter(query) {
      const lowerQuery = query.toLowerCase()
      let items = this.allItemsNotFiltered
      const inputElement =
        this.$refs.autoCompleteInput?.$el?.querySelector('input')
      let cursorPosition = 0

      if (inputElement) {
        cursorPosition = inputElement.selectionStart
      }
      this.showPath = false
      if (!query) {
        items = this.allItems
        this.showPath = true
      }
      this.$nextTick(() => {
        if (inputElement) {
          inputElement.focus()
          inputElement.setSelectionRange(cursorPosition, cursorPosition)
        }
      })
      this.filteredItems = items.filter((item) => {
        const name = item.name ? item.name.toLowerCase() : ''
        const title = item.title ? item.title.toLowerCase() : ''

        if (item.status === 'CONVERTING') {
          return false
        }

        if (item.title) {
          return item.status === 'CONVERTED' && title.includes(lowerQuery)
        }

        if (item.name) {
          return name.includes(lowerQuery)
        }
        return false
      })
    },

    handleSelectedItem(item) {
      if (!this.checkIfSelected(item)) {
        this.selectedItems.push(item)
      }
    },

    handleFolderSelected(folder) {
      this.lastFolder = folder
      this.breadcrumbArray.push(folder)
    },

    async toggleSelectAll(isSelected) {
      let selectableItems = []

      if (this.lastFolder.id) {
        selectableItems = this.filteredItems.filter(
          (item) => !this.alreadyHasPandaAssistVideos.includes(item.id)
        )
      } else {
        selectableItems = this.flatItemList.filter(
          (item) => !this.alreadyHasPandaAssistVideos.includes(item.id)
        )
      }

      if (isSelected) {
        this.selectedItems = [
          ...new Set([...this.selectedItems, ...selectableItems]),
        ]
      } else {
        if (this.lastFolder.id) {
          this.selectedItems = this.selectedItems.filter(
            (item) =>
              !selectableItems.some((selectable) => selectable.id === item.id)
          )
        } else {
          this.selectedItems = []
        }
      }
      this.updateTotalMinutes()
    },

    formatDuration(seconds) {
      if (!seconds || seconds <= 0 || isNaN(seconds)) return ''

      const hrs = Math.floor(seconds / 3600)
      const mins = Math.floor((seconds % 3600) / 60)
      const secs = Math.floor(seconds % 60)

      if (hrs > 0) {
        return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(
          2,
          '0'
        )}:${String(secs).padStart(2, '0')}`
      } else {
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(
          2,
          '0'
        )}`
      }
    },
    calcCreditsPerVideo(item) {
      const hasSubtitle = item.subtitles && item.subtitles.length

      if (item.length && !hasSubtitle) {
        const minutes = Math.floor(item.length / 60)
        const seconds = Math.floor(item.length % 60)
        const credits_per_video = this.applyCreditsCalcRule({
          minutes,
          seconds,
        })
        return credits_per_video
      }

      return 0
    },
    async getFolderSize(
      folders,
      isSub = false,
      id = null,
      data = new Map(),
      visitedFolders = new Set()
    ) {
      for (const folder of folders) {
        if (visitedFolders.has(folder.id)) continue

        visitedFolders.add(folder.id)

        const videosIn = await window.db.videos
          .where({ folder_id: folder.id })
          .and(({ status }) => status !== 'DELETING')
          .toArray()

        let fData = { size: 0, length: 0, cost: 0 }

        for (const { storage_size, length } of videosIn) {
          fData.size += storage_size
          fData.length += length
          fData.cost += Math.ceil(length / 60) // Calcula o custo arredondando para cima
        }

        if (!isSub) {
          data.set(folder.id, fData)
        } else {
          const parentData = data.get(id) || { size: 0, length: 0, cost: 0 }

          parentData.size += fData.size
          parentData.length += fData.length
          parentData.cost += fData.cost

          data.set(id, parentData)
        }

        // Recursivamente processa subpastas
        const subFolders = this.folders.filter(
          ({ parent_folder_id }) => parent_folder_id === folder.id
        )

        if (subFolders.length) {
          await this.getFolderSize(
            subFolders,
            true,
            id ?? folder.id,
            data,
            visitedFolders
          )
        }
      }

      return data
    },
    async checkIfVideosAreLoaded() {
      const allVideos = await window.db.videos.toArray()
      if (!allVideos.length) {
        const { data } = await videosApi.getAllVideosAndBin()
        const videosResponse = data.videos
        let filteredFoldersIds = []
        for (let folderId in videosResponse) {
          filteredFoldersIds.push(folderId)
        }
        await window.db.videos.clear()
        for (let folderId of filteredFoldersIds) {
          let arrToBeAdded = videosResponse[folderId].map((obj) => ({
            ...obj,
            folder_id: folderId,
          }))
          await window.db.videos.bulkPut(arrToBeAdded)
        }
      }
    },
    handleDialogClose(isVisible) {
      this.internalDialogVisible = isVisible

      if (!isVisible) {
        this.resetModal()
      }
    },
    nextStep() {
      this.step += 1
      if (this.step == 5) {
        this.checkIfSelected(this.video_external)
        this.checkboxSelection(this.video_external)
      }
    },
    async nextStepOverview() {
      if (this.langOption === 'pt') {
        this.langOption = 'pt-BR'
      }

      this.isLoading = true
      try {
        if (this.isUpdateVideos) {
          const selectedIds = this.selectedItems
            .filter(
              (item) =>
                item.title && !this.isUpdateVideos.videos.includes(item.id)
            )
            .map((item) => item.id)

          const response = await pandaAssist.purchaseOverview({
            videoIds: selectedIds,
            lang: this.langOption,
          })

          this.purchaseOverviewData = response.data
        } else {
          const selectedIds = this.selectedItems
            .filter((item) => item.title)
            .map((item) => item.id)
          const response = await pandaAssist.purchaseOverview({
            videoIds: selectedIds,
            lang: this.langOption,
          })
          this.purchaseOverviewData = response.data
        }
        this.step += 1
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
      } finally {
        this.isLoading = false
      }
    },

    async confirmAndBuy() {
      if (this.langOption === 'pt') {
        this.langOption = 'pt-BR'
      }
      this.isConfirming = true
      this.$root.$emit('PandaAssistUpdateVideos', this.isConfirming)

      try {
        if (this.isUpdateVideos) {
          const combinedItems = [
            ...this.selectedItems,
            ...this.isUpdateVideos.videos,
          ]
            .filter(
              (item, index, self) =>
                self.findIndex((t) => t.id === item.id) === index
            )
            .map((item) => item.id)
          const assistantId = this.isUpdateVideos.id
          await pandaAssist.updateAssistantVideoBind(assistantId, {
            videoIds: combinedItems,
            lang: this.langOption,
          })
          this.$root.$emit('PandaAssistUpdateVideos')
        } else {
          const selectedIds = this.selectedItems
            .filter((item) => item.title)
            .map((item) => item.id)
          const response = await pandaAssist.buyAndCreate({
            videoIds: selectedIds,
            lang: this.langOption,
            name: this.name,
            openNewTab: false,
            initialQuestion: this.initialQuestion,
            question_suggestions: this.suggestionEnabled,
          })
          this.newAssistantId = response.data.assistant.id
          this.$root.$emit('PandaAssistUpdate')
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            type: 'success',
            text: i18n.t('message.assistSuccess'),
          })
        }

        this.step += 1
      } catch (error) {
        console.error('Erro ao processar:', error)
      } finally {
        this.isConfirming = false
      }
    },
    backStep() {
      this.step -= 1
    },
    toggleVideoSelection(videoId) {
      const index = this.selectedVideos.indexOf(videoId)
      if (index === -1) {
        this.selectedVideos.push(videoId)
      } else {
        this.selectedVideos.splice(index, 1)
      }
    },
    finalize() {
      console.log('Finalizado!', {
        name: this.name,
        initialQuestion: this.initialQuestion,
        suggestionEnabled: this.suggestionEnabled,
        selectedVideos: this.selectedVideos,
      })
      this.closeModal()
    },
    async goToPandaAssist() {
      this.$router.push({
        name: 'AgentPage',
        params: { id: this.newAssistantId },
      })
      this.$root.$emit('goToPandaAssist')
    },
    resetModal() {
      this.applyFilter('')
      this.searchQuery = ''
      if (this.isUpdateVideos?.id) {
        this.step = 5
        this.name = this.isUpdateVideos.name
        this.initialQuestion = this.isUpdateVideos.initial_question
        this.suggestionEnabled = true
        this.selectedVideos = this.isUpdateVideos.videos
        return
      }
      this.step = 0
      this.name = ''
      ;(this.initialQuestion = this.$t(
        'message.pages.pandaAssist.createModal.howCanIHelp'
      )),
        (this.suggestionEnabled = true)
      this.selectedVideos = []
      this.totalMinutes = 0
      this.selectedItems = []
    },
    async handleEmitAll() {
      this.selectedItems = []
      this.isShown = false
      this.$root.$emit('analyticsV2SearchAll')
    },
    async handleFilterSelected() {
      let videos = []
      let folders = []
      for (let item of this.selectedItems) {
        if (item.name) {
          folders.push(item)
        } else {
          videos.push(item)
        }
      }
      this.filteredVideos = [...videos]
      let result = await this.getAllItems(folders)
      this.filteredVideos = this.filteredVideos.concat(result)
      this.$root.$emit('filterSelected', this.filteredVideos)
      this.filteredVideos = []
      this.isShown = false
    },
    async getAllItems(folders) {
      let items = []
      for (let item of folders) {
        if (item.name) {
          let vids = await window.db.videos
            .where({ folder_id: item.id })
            .toArray()
          items = items.concat(vids)
          let children = this.folders.filter(
            (folder) => folder.parent_folder_id === item.id
          )
          if (children.length) {
            await this.getAllItems(children)
          }
        }
      }
      return items
    },
    async handleEnterFolder(item) {
      if (!item.name) {
        this.checkboxSelection(item)
      } else {
        this.allVideos = await window.db.videos
          .where({ folder_id: item.id })
          .toArray()

        let breadcrumbLocal = []

        const buildBreadcrumb = (folder) => {
          if (folder.parent_folder_id) {
            const parentFolder = this.folders.find(
              (f) => f.id === folder.parent_folder_id
            )
            if (parentFolder) {
              buildBreadcrumb(parentFolder)
            }
          }
          breadcrumbLocal.push(folder)
        }

        buildBreadcrumb(item)

        this.lastFolder = item
        this.breadcrumbArray = [
          { name: '', id: null, parent_folder_id: null },
          ...breadcrumbLocal,
        ]
        this.searchQuery = ''
        this.applyFilter('')
      }
    },
    checkIfHasChildren(folder) {
      let anySon = this.folders.find(
        (item) => item.parent_folder_id === folder.id
      )
      return anySon
    },
    async handleBackTo(backToFolder) {
      if (backToFolder.id) {
        this.allVideos = await window.db.videos
          .where({ folder_id: backToFolder.id })
          .toArray()
      } else {
        this.breadcrumbArray = [{ name: '', id: null, parent_folder_id: null }]
        this.getVideos()
      }
      this.selectedFolder = null
      this.lastFolder = backToFolder
      if (
        this.breadcrumbArray.length < 2 ||
        this.breadcrumbArray[this.breadcrumbArray.length - 1].id ===
          backToFolder.id
      )
        return
      let index = this.breadcrumbArray.findIndex((folder) => {
        return folder.id === backToFolder.id
      })
      let newArr = this.breadcrumbArray.slice(0, index + 1)
      this.breadcrumbArray = newArr
    },
    async getVideos() {
      this.allVideosNotFiltered = await window.db.videos.toArray()
      this.allVideos = await window.db.videos
        .where({ folder_id: 'root' })
        .toArray()

      this.filterByExternalID()
    },
    checkIfSelected(item) {
      let foundItem = this.selectedItems.find((searchedVideo) => {
        return searchedVideo.id === item.id
      })
      if (foundItem) return true
      return false
    },
    async getAllItemsInFolder(folderId) {
      let items = []

      // Adiciona todos os vídeos diretamente na pasta
      const videos = await window.db.videos
        .where({ folder_id: folderId })
        .and(({ status }) => status !== 'DELETING')
        .toArray()
      items.push(...videos)

      // Busca subpastas
      const subFolders = this.folders.filter(
        (folder) => folder.parent_folder_id === folderId
      )

      // Recursivamente adiciona itens das subpastas
      for (const subFolder of subFolders) {
        const subItems = await this.getAllItemsInFolder(subFolder.id)
        items.push(...subItems)
      }

      return items
    },
    async checkboxSelection(item) {
      if (!item) return
      if (this.alreadyHasPandaAssistVideos.includes(item.id)) {
        return
      }

      const alreadySelected = this.checkIfSelected(item)

      if (item.name) {
        const allItemsInFolder = await this.getAllItemsInFolder(item.id)

        if (alreadySelected) {
          this.selectedItems = this.selectedItems.filter(
            (selectedItem) =>
              selectedItem.id !== item.id &&
              !allItemsInFolder.some((i) => i.id === selectedItem.id)
          )
        } else {
          const itemsToAdd = allItemsInFolder.filter(
            (i) =>
              !this.checkIfSelected(i) &&
              !this.alreadyHasPandaAssistVideos.includes(i.id)
          )
          this.selectedItems = [...this.selectedItems, item, ...itemsToAdd]
        }
      } else {
        if (alreadySelected) {
          this.selectedItems = this.selectedItems.filter(
            (selected) => selected.id !== item.id
          )
        } else {
          this.selectedItems.push(item)
        }
      }
      this.selectedItems = [...new Set(this.selectedItems)]
      this.updateTotalMinutes()
    },
    applyCreditsCalcRule(obj) {
      let roundPriceUp = null
      if (obj.minutes < 5) return 1

      const remainder = obj.minutes % 5
      if (remainder !== 0) {
        roundPriceUp = true
      } else {
        roundPriceUp = obj.seconds > 0
      }
      const creditsPerMin = Math.floor(obj.minutes / 5)
      const credits_per_video = roundPriceUp ? creditsPerMin + 1 : creditsPerMin
      return credits_per_video
    },
    updateTotalMinutes() {
      this.totalMinutes = this.selectedItems.reduce((total, item) => {
        const hasSubtitle = item.subtitles && item.subtitles.length

        if (item.length && !hasSubtitle) {
          const minutes = Math.floor(item.length / 60)
          const seconds = Math.floor(item.length % 60)
          const credits_per_video = this.applyCreditsCalcRule({
            minutes,
            seconds,
          })
          return total + credits_per_video
        }
        return total
      }, 0)
    },
    async filterByExternalID() {
      if (!this.video_external_id) return

      const option = { video_external_id: this.video_external_id }

      const [videoInDB] = await window.db.videos.where(option).toArray()
      const videoInRoot = _.find(this.allVideos, option)

      let video

      if (videoInRoot) video = videoInRoot
      else {
        this.allVideos.push(videoInDB)
        video = videoInDB
      }

      this.selectedItems = [video]

      this.handleFilterSelected()
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/src/data-tableV2';
.selected-video {
  background-color: #e0f7fa !important;
}

.assistHeadline {
  white-space: normal;
  word-break: break-word;
}

.step-indicators {
  display: flex;
  gap: 8px;
}

::v-deep .item-line {
  border-top: 1px solid varFigma(neutral-95-lightest) !important;
}

.checkoutStep {
  height: 100%;
}

.chatPreview {
  width: 327px;
  overflow: auto;
  height: 211px;
  margin: 0 auto;
  background: #282931;
  border-radius: 8px;
  border: 1px solid varFigma(neutral-80-lighter);
}

.negativeCreditsIcon {
  color: varFigma(feedback-danger-light) !important;
}

.dangerCreditsText {
  color: varFigma(feedback-danger) !important;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
  display: inline-block;
}

.checkoutDesc {
  background: varFigma(neutral-background);
}

.checkoutPayment {
  background: varFigma(neutral-100-white);

  /* shadow */
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.15);
}

.modalFooter {
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 10;
  border-top: 1px solid varFigma(brand-95-lighter);
  background: varFigma(neutral-99);
  padding: 24px 28px;
}

.checkoutFooter {
  position: sticky;
  display: grid;
  bottom: 0;
  width: 100%;
  z-index: 10;
  border-top: 1px solid varFigma(neutral-95-lightest);
  background: varFigma(brand-99-lightest);
}

::v-deep .panda-search:before {
  font-size: 16px !important;
}

.base-input.v-input[data-v-36a1b07e] input::placeholder {
  font-weight: 400;
  font-size: 12px;
}

.v-dialog--fullscreen > .v-card {
  min-height: auto !important;
  min-width: 100%;
  margin: 0 !important;
  padding: 16px;
}

.step-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: varFigma(neutral-80-lighter);
  transition: background 0.3s ease;
}

::v-deep .v-label {
  font-size: 14px !important;
  color: varFigma(neutral-40-light) !important;
}

.extractBtn {
  color: varFigma(brand-60-base);
}

::v-deep .t-header {
  display: none !important;
}

.tableArrow {
  color: varFigma(neutral-20-dark) !important;
}

.step-indicator.active {
  background: varFigma(brand-60-base); /* Cor para o passo ativo */
}

.message-box {
  display: block;
  color: #636578 !important;
  z-index: 999;
  position: relative;
}

.folderIcon {
  color: varFigma(neutral-70) !important;
}

.videoIcon {
  color: varFigma(brand-70) !important;
}

.videoIconNeutral {
  color: varFigma(neutral-70) !important;
}

.creditsIcon {
  color: varFigma(brand-70) !important;
}

.questionSugestion {
  color: #636578;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 16.5px */
  letter-spacing: 0.66px;
  text-transform: uppercase;
}

::v-deep .panda-assist-dialog {
  background: varFigma(neutral-100-white) !important;
}
</style>
